import {
  CSSProperties,
  Fragment,
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useId,
  useRef
} from 'react';
import { createPortal } from 'react-dom';
import CloseIcon from '@mui/icons-material/Close';

import ForcePortrait from 'components/ForcePortrait/ForcePortrait';
import { RUN_VARIANT } from 'components/UserActions/MultiRunActions/runVariants';
import { actionVariants } from 'components/UserActions/PokerActions/actionVariants';

import { MULTI_RUN_INTRO_COPY, MultiRunIntroContent } from './multiRunIntroCopy';
import { RUN_COUNTS, RunCount, runItLabel, runItSpokenLabel } from './runVariants';

import styles from './MultiRunIntroModal.module.scss';

/**
 * The one-time multi-run introduction (EN-10791).
 *
 * The lobby's Promo Entry Modal in a different role: same panel, title,
 * parchment card, primary button and fine print (components/PlayerPromos/
 * PromoEntryModal.tsx), so the two read as one family. What differs on purpose:
 *   - one action, BROWSE TABLES: a second "got it" would land in the same place;
 *   - no "Don't show me this again" checkbox: it is one-time by definition;
 *   - no glint/sparkle loops (motion fence), one entrance instead;
 *   - dialog semantics the promo lacks: role, labelled title, focus moved in
 *     and trapped, Escape, focus restored on close;
 *   - a backdrop tap is ignored for the first moments after it opens, so a
 *     stray tap as it paints cannot burn the only showing.
 *
 * Pure props. What it says arrives as `content` — an edition's copy merged
 * over the timeless copy (resolveMultiRunIntroContent), rendered as plain
 * text. When it shows and what "seen" means live in MultiRunIntroAutoPrompt;
 * this component only reports how it was dismissed.
 */
// How the player closed it. BROWSE TABLES also takes them to the Cash tab — the
// caller does that (multiRunIntroNavigation), not this component.
export type MultiRunIntroDismissReason = 'close' | 'escape' | 'backdrop' | 'browse-tables';

export const BACKDROP_TAP_GUARD_MS = 600;

type MultiRunIntroModalProps = {
  onDismiss: (reason: MultiRunIntroDismissReason) => void;
  /** The edition's headline, availability and small print, over the timeless copy. */
  content: MultiRunIntroContent;
  backdropGuardMs?: number;
};

const FOCUSABLE = 'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';

// Each entry on a line of its own; the space between them keeps the text whole
// for a screen reader ("…every stake. Omaha coming soon!").
const Lines = ({ lines, className }: { lines: readonly string[]; className: string }) => (
  <>
    {lines.map((line, index) => (
      <Fragment key={index}>
        {index > 0 && ' '}
        <span className={className}>{line}</span>
      </Fragment>
    ))}
  </>
);

// Chip colors come from the table's own RUN IT map (RUN_VARIANT) and its
// action-button variants, so the modal teaches exactly the colors players meet
// at the felt. Passed as custom properties; everything else about the chip
// lives in the stylesheet.
const chipStyle = (count: RunCount): CSSProperties => {
  const variant = actionVariants[RUN_VARIANT[count]];
  return {
    ['--chip-bg' as string]: variant.bg,
    ['--chip-border' as string]: variant.border,
    ['--chip-dash' as string]: variant.dash,
    ['--chip-text' as string]: variant.text,
    ['--chip-shadow' as string]: variant.shadow
  } as CSSProperties;
};

const MultiRunIntroModal = ({
  onDismiss,
  content,
  backdropGuardMs = BACKDROP_TAP_GUARD_MS
}: MultiRunIntroModalProps) => {
  const copy = MULTI_RUN_INTRO_COPY;
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const openedAtRef = useRef(0);
  const dismissedRef = useRef(false);

  // One dismissal only: a click and an Escape in the same beat must not report
  // twice (the prompt writes "seen" and emits analytics on the first).
  const dismiss = useCallback(
    (reason: MultiRunIntroDismissReason) => {
      if (dismissedRef.current) return;
      dismissedRef.current = true;
      onDismiss(reason);
    },
    [onDismiss]
  );

  // Move focus in on open and give it back on close (the promo does neither).
  useEffect(() => {
    openedAtRef.current = Date.now();
    const previouslyFocused =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    dialogRef.current?.focus({ preventScroll: true });
    return () => {
      if (previouslyFocused?.isConnected) previouslyFocused.focus({ preventScroll: true });
    };
  }, []);

  // Same body scroll lock as the promo modal, scrollbar gutter included.
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const previousPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.paddingRight = previousPaddingRight;
    };
  }, []);

  const handleBackdropClick = () => {
    if (Date.now() - openedAtRef.current < backdropGuardMs) return;
    dismiss('backdrop');
  };

  const stopPropagation = (event: ReactMouseEvent) => event.stopPropagation();

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      dismiss('escape');
      return;
    }
    if (event.key !== 'Tab' || !dialogRef.current) return;

    // Keep Tab inside the dialog: wrap from the last control to the first and
    // back, and pull focus in if it starts on the dialog itself.
    const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && (active === first || active === dialogRef.current)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  // Rotates WITH the portrait-locked lobby on touch-landscape, exactly as the
  // promo does: it portals to <body>, so the rotation lives inside the portal.
  // It only ever opens in the lobby (see MultiRunIntroAutoPrompt), so it never
  // rotates over a landscape table.
  return createPortal(
    <ForcePortrait>
      <div
        className={styles.backdrop}
        data-testid='multirun-intro-backdrop'
        onClick={handleBackdropClick}>
        <div
          ref={dialogRef}
          className={styles.panel}
          role='dialog'
          aria-modal='true'
          aria-labelledby={titleId}
          tabIndex={-1}
          onClick={stopPropagation}
          onKeyDown={handleKeyDown}>
          <div className={styles.header}>
            <h2 id={titleId} className={styles.title}>
              <Lines lines={content.titleLines} className={styles.titleLine} />
            </h2>
            <button
              type='button'
              className={styles.close}
              aria-label={copy.closeLabel}
              onClick={() => dismiss('close')}>
              <CloseIcon fontSize='inherit' />
            </button>
          </div>

          <section className={styles.card} aria-label={copy.cardHeading}>
            <span className={styles.cardFrame} aria-hidden='true' />
            <div className={styles.cardMeta}>
              <span>{copy.cardHeading}</span>
              <span>{copy.cardTag}</span>
            </div>
            <ul className={styles.chips} aria-label={copy.chipsLabel}>
              {RUN_COUNTS.map((count) => (
                <li
                  key={count}
                  className={styles.chip}
                  style={chipStyle(count)}
                  aria-label={runItSpokenLabel(count)}>
                  <span aria-hidden='true'>{runItLabel(count)}</span>
                </li>
              ))}
            </ul>
            <ol className={styles.steps}>
              {copy.steps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
            <div className={styles.cardFoot}>{copy.cardFoot}</div>
          </section>

          <p className={styles.message}>
            <Lines lines={content.availability} className={styles.messageLine} />
          </p>

          <button type='button' className={styles.primary} onClick={() => dismiss('browse-tables')}>
            {copy.action}
          </button>

          <p className={styles.smallPrint}>{content.smallPrint}</p>
        </div>
      </div>
    </ForcePortrait>,
    document.body
  );
};

export default MultiRunIntroModal;
